/**
 * Versioned persistence of a small part of the store in localStorage.
 *
 * Only what must survive a reload is kept (preferences, unsent work), never
 * server data or tokens. The stored shape carries a version; when the shape
 * changes, a migration upgrades old data instead of discarding or misreading it.
 */

/** Upgrades state from version N-1 to the version it is registered under. */
export type Migration = (state: Record<string, unknown>) => Record<string, unknown>;

export interface PersistenceOptions<T extends object> {
  key: string;
  version: number;
  /** Keyed by the version they produce: { 2: v1 -> v2, 3: v2 -> v3 }. */
  migrations?: Record<number, Migration>;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Minimum time between writes. */
  throttleMs?: number;
  /** Type guard for the fully migrated state; a failing check discards it. */
  validate?: (state: unknown) => state is T;
}

interface Envelope {
  version: number;
  state: Record<string, unknown>;
}

function isEnvelope(value: unknown): value is Envelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Envelope).version === 'number' &&
    typeof (value as Envelope).state === 'object' &&
    (value as Envelope).state !== null
  );
}

export function createPersistence<T extends object>(options: PersistenceOptions<T>) {
  const { key, version, migrations = {}, throttleMs = 400, validate } = options;
  const storage = options.storage ?? globalThis.localStorage;

  /** The saved state brought up to the current version, or undefined if there is none. */
  function load(): T | undefined {
    try {
      const raw = storage.getItem(key);
      if (!raw) return undefined;
      const envelope: unknown = JSON.parse(raw);
      if (!isEnvelope(envelope) || envelope.version > version) return undefined;
      let state = envelope.state;
      for (let target = envelope.version + 1; target <= version; target += 1) {
        const migrate = migrations[target];
        if (!migrate) return undefined;
        state = migrate(state);
      }
      if (validate && !validate(state)) return undefined;
      return state as T;
    } catch {
      return undefined; // unreadable or blocked storage: start fresh
    }
  }

  function save(state: T): void {
    try {
      storage.setItem(key, JSON.stringify({ version, state }));
    } catch {
      // Quota exceeded or storage disabled: the app works without persistence.
    }
  }

  /** Save `select(getState())` whenever it changes, at most once per throttle interval. */
  function attach(store: { getState(): unknown; subscribe(listener: () => void): () => void }, select: (state: never) => T) {
    let last = select(store.getState() as never);
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      save(last);
    };
    const unsubscribe = store.subscribe(() => {
      const next = select(store.getState() as never);
      if (shallowEqual(next, last)) return;
      last = next;
      timer ??= setTimeout(flush, throttleMs);
    });
    // Do not lose the last change when the tab is closed within the throttle window.
    const onHide = () => {
      if (timer) {
        clearTimeout(timer);
        flush();
      }
    };
    globalThis.addEventListener?.('pagehide', onHide);
    return () => {
      unsubscribe();
      globalThis.removeEventListener?.('pagehide', onHide);
      onHide();
    };
  }

  return { load, save, attach, clear: () => storage.removeItem(key) };
}

function shallowEqual(a: object, b: object): boolean {
  const keys = Object.keys(a) as (keyof typeof a)[];
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}
